import { ToggleOff, ToggleOn } from '@mui/icons-material';
import { FilterIcon, X } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FilterOption } from '../../types/dashboard';

interface FilterBarProps {
  filter: string;
  onChangeFilter: (filter: string) => void;
  loading: boolean;
  error: string | null;
  filterOptions: FilterOption[];
  filtersSelected?: FilterOption[];
  isApplyForAll?: boolean;
  onFilterOpened?: () => void;
  onFilterSelect?: (filter: FilterOption) => void;
  onFilterUnselect?: (filter: FilterOption) => void;
  onApplyFilters?: (isApplyForAll: boolean) => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filter,
  onChangeFilter,
  loading,
  filterOptions,
  error: _error,
  filtersSelected = [],
  isApplyForAll: isApplyForAllProp = true,
  onFilterOpened,
  onFilterSelect,
  onFilterUnselect,
  onApplyFilters,
}) =>
{
  const [isFilterOpened, setIsFilterOpened] = useState<boolean>(false);
  const [isMenuFilterOpened, setIsMenuFilterOpened] = useState<boolean>(false);
  const [isApplyForAll, setIsApplyForAll] = useState<boolean>(isApplyForAllProp);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [hiddenCount, setHiddenCount] = useState<number>(0);
  const [activeFilterIdx, setActiveFilterIdx] = useState<number>(0);
  const [dropdownLeft, setDropdownLeft] = useState<number>(0);

  const skipFocusRef = useRef<boolean>(false);
  const inputFilterBar = useRef<HTMLInputElement>(null);
  const filterBarContainerRef = useRef<HTMLDivElement>(null);
  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  // True while the highlight is being driven by the keyboard. Blocks hover from
  // stealing the selection when the list scrolls under a stationary cursor.
  const keyboardNavRef = useRef<boolean>(false);

  useEffect(() =>
  {
    setIsApplyForAll(isApplyForAllProp);
  }, [isApplyForAllProp]);

  useEffect(() =>
  {
    if (isFilterOpened)
    {
      inputFilterBar.current?.focus();
    }
  }, [isFilterOpened]);

  // Close filter dropdown and expanded chips panel on outside click or Escape key
  useEffect(() =>
  {
    if (!isMenuFilterOpened && !isExpanded) return;
    const handleClickOutside = (e: MouseEvent) =>
    {
      if (
        filterBarContainerRef.current &&
        !filterBarContainerRef.current.contains(e.target as Node)
      )
      {
        setIsMenuFilterOpened(false);
        setIsExpanded(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape')
      {
        setIsMenuFilterOpened(false);
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () =>
    {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuFilterOpened, isExpanded]);

  // Count chips that overflow below the first row
  useLayoutEffect(() =>
  {
    const container = chipsContainerRef.current;
    if (!container || filtersSelected.length === 0)
    {
      setHiddenCount(0);
      setIsExpanded(false);
      return;
    }
    // Remove height constraint so all chips lay out naturally for accurate measurement
    container.style.maxHeight = 'none';
    const chips = Array.from(container.children) as HTMLElement[];
    const firstChip = chips[0];
    if (!firstChip) { setHiddenCount(0); setIsExpanded(false); return; }
    const firstOffsetTop = firstChip.offsetTop;
    const hidden = chips.filter(chip => chip.offsetTop > firstOffsetTop).length;
    // Clip to exact chip height to prevent partial-chip slivers
    container.style.maxHeight = `${firstChip.offsetHeight}px`;
    setHiddenCount(hidden);
    if (hidden === 0) setIsExpanded(false);
  }, [filtersSelected, isFilterOpened]);

  useLayoutEffect(() =>
  {
    if (isMenuFilterOpened && inputFilterBar.current)
    {
      setDropdownLeft(inputFilterBar.current.offsetLeft);
    }
  }, [isMenuFilterOpened]);

  const onHandlerFilterSelect = (filter: FilterOption) =>
  {
    onFilterSelect?.(filter);
    onChangeFilter('');
  };

  const onHandlerFilterUnselect = (filter: FilterOption) =>
  {
    skipFocusRef.current = true;
    setIsMenuFilterOpened(false);
    onFilterUnselect?.(filter);
  };

  // Options currently shown in the dropdown (excludes already-selected ones)
  const availableOptions = filterOptions.filter(
    opt => !filtersSelected.some(f => f.Id === opt.Id && f.Name === opt.Name),
  );

  // Highlight the first option whenever the list changes
  useEffect(() =>
  {
    setActiveFilterIdx(0);
    if (optionsListRef.current) optionsListRef.current.scrollTop = 0;
  }, [filter, filterOptions, filtersSelected]);

  // Keep the highlighted option visible inside the scrollable dropdown
  useEffect(() =>
  {
    if (!keyboardNavRef.current) return;

    const list = optionsListRef.current;
    const option = optionRefs.current[activeFilterIdx];
    if (!list || !option) return;

    const optionTop = option.offsetTop;
    const optionBottom = optionTop + option.offsetHeight;

    if (optionTop < list.scrollTop)
    {
      list.scrollTop = optionTop;
    }
    else if (optionBottom > list.scrollTop + list.clientHeight)
    {
      list.scrollTop = optionBottom - list.clientHeight;
    }
  }, [activeFilterIdx, isMenuFilterOpened]);

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (!isMenuFilterOpened || availableOptions.length === 0) return;

    if (e.key === 'ArrowDown')
    {
      e.preventDefault();
      keyboardNavRef.current = true;
      setActiveFilterIdx(prev => (prev + 1) % availableOptions.length);
    }
    else if (e.key === 'ArrowUp')
    {
      e.preventDefault();
      keyboardNavRef.current = true;
      setActiveFilterIdx(
        prev => (prev - 1 + availableOptions.length) % availableOptions.length,
      );
    }
    else if (e.key === 'Enter')
    {
      e.preventDefault();
      const selected = availableOptions[activeFilterIdx];
      if (selected)
      {
        onHandlerFilterSelect(selected);
      }
    }
    else if (e.key === 'Escape')
    {
      setIsMenuFilterOpened(false);
    }
  };

  const chipStyle = {
    backgroundColor: document.documentElement.classList.contains('dark') ? '#4a4c4c' : '#e5e7eb',
    color: document.documentElement.classList.contains('dark') ? '#e5e7eb' : '#374151',
  };

  const isDark = document.documentElement.classList.contains('dark');

  return (
    <>
      <div className="flex items-center gap-1 pr-2 py-1">
        {isFilterOpened ? (
          <div
            ref={filterBarContainerRef}
            style={{ maxWidth: '700px' }}
            className="bg-white dark:bg-[#333535] border border-[#9f9f9f] dark:border-[#4a4c4c] relative flex items-center min-h-8 gap-3 px-2 my-1 rounded-md"
          >
            {/* Filter icon */}
            <button
              onClick={() =>
              {
                setIsFilterOpened(false);
                onChangeFilter('');
              }}
              className="flex-shrink-0 text-gray-400 hover:text-gray-400"
            >
              <FilterIcon
                fill={isDark ? '#3c6c80' : '#62aacb'}
                color={isDark ? '#3c6c80' : '#62aacb'}
                className="w-3.5 h-3.5"
              />
            </button>
            {/* Apply for all toggle */}
            <button
              onClick={() =>
              {
                setIsApplyForAll(!isApplyForAll);
              }}
              className="flex-shrink-0 flex items-center gap-1.5 text-xs whitespace-nowrap"
              style={{
                color: isApplyForAll
                  ? isDark
                    ? '#3c6c80'
                    : '#62aacb'
                  : isDark
                    ? '#6e6860'
                    : 'rgba(0, 0, 0, 0.4)',
              }}
            >
              {isApplyForAll ? (
                <ToggleOn style={{ fontSize: '18px' }} />
              ) : (
                <ToggleOff style={{ fontSize: '18px' }} />
              )}
              <span>Apply for all</span>
            </button>
            {/* Separator */}
            <div className="flex-shrink-0 w-px h-4 bg-gray-300 dark:bg-[#4a4c4c]"></div>
            {/* Input */}
            <input
              ref={inputFilterBar}
              type="text"
              placeholder="Add a Filter"
              className="flex-shrink-0 py-1.5 w-28 text-xs focus:outline-none bg-white dark:bg-[#333535] dark:text-[#a9a093] dark:placeholder:text-[#6e6860]"
              onChange={e =>
              {
                onChangeFilter(e.currentTarget.value);
                setIsMenuFilterOpened(true);
                setIsExpanded(false);
              }}
              onKeyDown={onInputKeyDown}
              onFocus={() =>
              {
                if (skipFocusRef.current)
                {
                  skipFocusRef.current = false;
                  return;
                }
                setIsMenuFilterOpened(true);
                setIsExpanded(false);
                onFilterOpened?.();
              }}
              onClick={() =>
              {
                if (!isMenuFilterOpened)
                {
                  setIsMenuFilterOpened(true);
                  setIsExpanded(false);
                  onFilterOpened?.();
                }
              }}
              value={filter}
            />
            {/* Selected filter chips — single row, clipped to exact chip height to avoid partial slivers */}
            {filtersSelected.length > 0 && (
              <div
                ref={chipsContainerRef}
                className="flex items-center gap-1 flex-wrap overflow-hidden flex-1 min-w-0"
                style={{ maxHeight: '26px' }}
              >
                {filtersSelected.map((filterSelected, idx) => (
                  <button
                    key={idx}
                    className="flex items-center gap-1 px-2 py-0.5 text-xs rounded whitespace-nowrap flex-shrink-0"
                    style={chipStyle}
                  >
                    <span className="text-xs">{filterSelected.Name}</span>
                    <X
                      onClick={() => onHandlerFilterUnselect(filterSelected)}
                      className="w-3 h-3 cursor-pointer"
                    />
                  </button>
                ))}
              </div>
            )}
            {/* Trailing buttons: Show X More / Show Less + Filter */}
            <div className="flex-shrink-0 flex items-center gap-2 ml-2">
              <span
                onClick={() =>
                {
                  if (hiddenCount === 0 && !isExpanded) return;
                  skipFocusRef.current = true;
                  setIsExpanded(prev => !prev);
                  setIsMenuFilterOpened(false);
                }}
                className="text-xs cursor-pointer whitespace-nowrap"
                style={{
                  color: isDark ? '#3c6c80' : '#62aacb',
                  visibility: (hiddenCount > 0 || isExpanded) ? 'visible' : 'hidden',
                }}
              >
                {isExpanded ? 'Show Less' : `Show ${hiddenCount} More`}
              </span>
              <button
                onClick={() =>
                {
                  onChangeFilter('');
                  setIsFilterOpened(false);
                  setIsExpanded(false);
                  onApplyFilters?.(isApplyForAll);
                }}
                style={{ backgroundColor: isDark ? '#3c6c80' : '#62aacb' }}
                className="px-2.5 py-0.5 text-white hover:text-white text-xs"
              >
                Filter
              </button>
            </div>
            {/* Expanded chips panel — drops below the whole bar so it never covers the trailing buttons */}
            {isExpanded && (
              <div
                className="absolute z-30 left-0 right-0 border border-gray-300 dark:border-[#4a4c4c] bg-white dark:bg-[#333535] shadow-lg p-2 overflow-y-auto"
                style={{ top: '100%', maxHeight: '150px' }}
              >
                <div className="flex flex-wrap gap-1">
                  {filtersSelected.map((filterSelected, idx) => (
                    <button
                      key={idx}
                      className="flex items-center gap-1 px-2 py-0.5 text-xs rounded whitespace-nowrap flex-shrink-0"
                      style={chipStyle}
                    >
                      <span className="text-xs">{filterSelected.Name}</span>
                      <X
                        onClick={() => onHandlerFilterUnselect(filterSelected)}
                        className="w-3 h-3 cursor-pointer"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Filter options dropdown */}
            {isMenuFilterOpened && (
              <div
                ref={optionsListRef}
                onMouseMove={() =>
                {
                  keyboardNavRef.current = false;
                }}
                className="w-56 max-h-64 min-h-10 overflow-auto z-20 absolute border border-gray-300 dark:border-[#4a4c4c] bg-white dark:bg-[#333535] shadow-lg"
                style={{ top: '100%', left: dropdownLeft - 13 }}
              >
                {loading && (
                  <div
                    className="px-0.5 py-1.5 w-full cursor-default inline-flex"
                    style={{ fontSize: '12px', color: isDark ? '#6e6860' : '#6b7280' }}
                  >
                    <span className="pl-2">Searching</span>
                  </div>
                )}
                {availableOptions.length > 0
                  ? availableOptions.map((filterOpt, idx) =>
                  {
                    const isActive = activeFilterIdx === idx;
                    return (
                      <div
                        key={idx}
                        ref={el =>
                        {
                          optionRefs.current[idx] = el;
                        }}
                        onClick={() => onHandlerFilterSelect(filterOpt)}
                        onMouseEnter={() =>
                        {
                          if (keyboardNavRef.current) return;
                          setActiveFilterIdx(idx);
                        }}
                        className="m-0.5 px-0.5 py-0.5 cursor-pointer rounded-sm"
                        style={{
                          fontSize: '10px',
                          backgroundColor: isActive ? '#7cc5dc' : isDark ? '#333535' : 'white',
                          color: isActive ? 'white' : isDark ? '#a9a093' : '#6b7280',
                        }}
                      >
                        <span className="pl-2">{filterOpt.Name}</span>
                      </div>
                    );
                  })
                  : filterOptions.length === 0 &&
                    !loading && (
                    <div
                      className="px-4 py-1.5 w-full cursor-default inline-flex"
                      style={{ fontSize: '12px', color: isDark ? '#6e6860' : '#6b7280' }}
                    >
                      <span className="pl-2">No results found</span>
                    </div>
                  )}
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() =>
            {
              setIsFilterOpened(true);
              setIsMenuFilterOpened(true);
              inputFilterBar.current?.focus();
              onChangeFilter('');
              onFilterOpened?.();
            }}
            className="flex items-center gap-1.5 pl-2 pr-2 text-gray-400 dark:text-[#6e6860] hover:text-gray-400"
            style={{ maxWidth: '200px' }}
          >
            <FilterIcon
              fill={
                filtersSelected.length > 0
                  ? isDark
                    ? '#3c6c80'
                    : '#62aacb'
                  : isDark
                    ? '#6e6860'
                    : 'gray'
              }
              color={
                filtersSelected.length > 0
                  ? isDark
                    ? '#3c6c80'
                    : '#62aacb'
                  : isDark
                    ? '#6e6860'
                    : 'gray'
              }
              className="w-3.5 h-3.5 flex-shrink-0"
            />
            {filtersSelected.length > 0 && (
              <span
                className="text-gray-600 dark:text-[#a9a093] truncate"
                style={{ fontSize: '10px', lineHeight: '1.2' }}
              >
                {filtersSelected.map(x => x.Name).join(', ')}
              </span>
            )}
          </button>
        )}
      </div>
    </>
  );
};
